/**
 * Story 24.3 — the class strings the shared Sheet, Dialog and AlertDialog
 * share (DESIGN.md › Components › Sheet and Dialog). Roles only.
 *
 * - Every layer is `bg-surface-raised` with a `border-line-floating` edge and
 *   `shadow-float-lg`, over a `bg-scrim` overlay. `z-50` only (the ladder's
 *   modal rung).
 * - Below 1024px (`lg`) a Dialog is a bottom sheet: full width, `rounded-t-xl`
 *   top corners, at most 90dvh, a decorative grab handle. At `lg` it is
 *   centred, at most 480px wide, `rounded-xl`.
 * - Motion collapses under `motion-reduce`.
 */

export const LAYER_SCRIM =
  'fixed inset-0 z-50 bg-scrim data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none';

export const LAYER_SURFACE =
  'fixed z-50 border-line-floating bg-surface-raised text-ink shadow-float-lg outline-none motion-reduce:transition-none motion-reduce:animate-none motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none';

/** The bottom-sheet shape every layer takes below `lg`. */
export const LAYER_BOTTOM_SHEET =
  'inset-x-0 bottom-0 max-h-[90dvh] w-full overflow-y-auto overscroll-contain rounded-t-xl border-t';

/** A Dialog / AlertDialog: bottom sheet below `lg`, centred at `lg` and up. */
export const LAYER_DIALOG = [
  LAYER_BOTTOM_SHEET,
  'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out',
  'max-lg:data-[state=open]:slide-in-from-bottom max-lg:data-[state=closed]:slide-out-to-bottom',
  'lg:inset-x-auto lg:top-1/2 lg:bottom-auto lg:left-1/2 lg:max-h-[85dvh] lg:w-[calc(100%-2rem)] lg:max-w-[480px] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-xl lg:border',
  'lg:data-[state=open]:fade-in-0 lg:data-[state=open]:zoom-in-95 lg:data-[state=closed]:fade-out-0 lg:data-[state=closed]:zoom-out-95',
].join(' ');

/** The decorative grab handle drawn at the top of a bottom sheet. */
export const LAYER_HANDLE =
  'pointer-events-none absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-line-strong';

/**
 * The always-present close X: a 44px ghost icon button, top-right, that
 * stays in view while the body scrolls.
 *
 * The layer's content is itself the scroll container (`overflow-y-auto`, and
 * callers add `max-h-… overflow-y-auto`), so an `absolute` X scrolled away
 * with the body. It is now the content's FIRST child, `sticky` 8px from the
 * top and right edges, with negative margins that take it out of the flow: the
 * next item starts exactly where it would without the X. Initial focus still
 * skips it (`[data-slot$="-close-x"]`), and the headers still leave room.
 *
 * The margins and the sticky insets depend on the content's padding and flex
 * gap, so each layer picks one of the variants below (8px from the top and
 * right edges). A sticky inset is measured from the scroll container's
 * CONTENT box, not its padding box: in a `p-6` container `top-2` pins the X
 * 24px + 8px in. So the padded variant uses `-top-4 -right-4` (24px - 16px =
 * 8px), matching its `-mt-4 -mr-4` resting place. jsdom has no layout, so
 * only a real browser shows a wrong inset.
 *
 * It has an opaque `bg-surface-raised` backing: body text that scrolls
 * beneath it is covered, never drawn through the glyph.
 */
export const LAYER_CLOSE_X_STICKY = 'sticky z-10 shrink-0 self-end bg-surface-raised';

/** Unpadded flex column, no gap (the responsive Sheet). */
export const LAYER_CLOSE_X_FLUSH = 'top-2 right-2 mt-2 mr-2 -mb-[52px]';

/** Unpadded flex column with `gap-4` (a Sheet with an explicit `side`). */
export const LAYER_CLOSE_X_FLUSH_GAP4 = 'top-2 right-2 mt-2 mr-2 -mb-[68px]';

/** `p-6` flex column with `gap-4` (the Dialog). */
export const LAYER_CLOSE_X_PADDED_GAP4 = '-top-4 -right-4 -mt-4 -mr-4 -mb-11';

/** Title: the fallback focus target (`tabIndex={-1}`), never ringed. */
export const LAYER_TITLE = 'type-heading text-ink outline-none';

export const LAYER_DESCRIPTION = 'type-body-sm text-ink-muted';
